import React from 'react'
import { Link } from 'react-router-dom'
import { Shield, Zap, Clock, CheckCircle, ArrowRight, Users, DollarSign, Lock, CreditCard, TrendingUp, Award, Globe, Smartphone, HeadphonesIcon, Building2, Banknote, Activity } from 'lucide-react'

const Home = () => {
  const features = [
    {
      icon: Shield,
      title: 'FDIC Insured',
      description: 'Your deposits are protected up to $250,000 by the Federal Deposit Insurance Corporation.'
    },
    {
      icon: Zap,
      title: 'Instant Transfers',
      description: 'Send and receive money instantly with our real-time payment processing system.'
    },
    {
      icon: Clock,
      title: '24/7 Banking',
      description: 'Access your accounts anytime, anywhere with our mobile and online banking platforms.'
    },
    {
      icon: DollarSign,
      title: 'Zero Fees',
      description: 'No monthly maintenance fees, no minimum balance requirements, no hidden charges.'
    },
    {
      icon: CreditCard,
      title: 'Premium Cards',
      description: 'Visa and Mastercard debit cards with cashback rewards and worldwide acceptance.'
    },
    {
      icon: TrendingUp,
      title: 'High Yield Savings',
      description: 'Earn competitive interest rates on your savings with our high-yield accounts.'
    },
    {
      icon: Lock,
      title: 'Bank-Grade Security',
      description: '256-bit encryption, multi-factor authentication, and biometric login protection.'
    },
    {
      icon: Globe,
      title: 'International Banking',
      description: 'Send money abroad with low fees and competitive exchange rates to over 50 countries.'
    }
  ]

  const stats = [
    { value: '250K+', label: 'Active Members', icon: Users },
    { value: '$2.5B+', label: 'Assets Under Management', icon: Banknote },
    { value: '99.9%', label: 'Service Uptime', icon: Activity },
    { value: '24/7', label: 'Customer Support', icon: HeadphonesIcon }
  ]

  const benefits = [
    'FDIC Insured up to $250,000',
    'Free online and mobile banking',
    'No minimum balance requirements',
    'Competitive interest rates up to 4.5% APY',
    'Free ATM access at 60,000+ locations nationwide',
    'Mobile check deposit',
    'Bill pay and automatic payments',
    'Fraud protection and zero liability',
    'Financial planning tools and calculators',
    'Early direct deposit - Get paid up to 2 days early'
  ]

  const products = [
    {
      icon: DollarSign,
      title: 'Checking Accounts',
      description: 'Free checking with no monthly fees',
      features: ['No minimum balance', 'Free debit card', 'Mobile banking']
    },
    {
      icon: TrendingUp,
      title: 'Savings Accounts',
      description: 'High-yield savings up to 4.5% APY',
      features: ['Competitive rates', 'No fees', 'Easy transfers']
    },
    {
      icon: CreditCard,
      title: 'Credit Cards',
      description: 'Rewards cards with cashback',
      features: ['Up to 5% cashback', 'No annual fee', 'Travel rewards']
    },
    {
      icon: Building2,
      title: 'Home Loans',
      description: 'Mortgages with low rates',
      features: ['FHA & VA loans', 'Low down payment', 'Fast approval']
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=1920&h=1080&fit=crop" 
            alt="Banking" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Award className="w-4 h-4" />
                <span className="text-sm font-medium">Rated #1 Digital Credit Union 2024</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Banking That Works For You
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Experience the future of banking with UniversalTrustCU. No fees, high rates, and the security you deserve. Join over 250,000 members nationwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" className="btn btn-lg bg-white text-blue-600 hover:bg-blue-50 shadow-xl">
                  Open Free Account
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/login" className="btn btn-lg btn-outline border-white text-white hover:bg-white/10">
                  Member Sign In
                </Link>
              </div>
              <p className="mt-6 text-sm text-blue-200">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                No credit check • 5-minute signup • FDIC insured
              </p>
            </div>

            <div className="hidden md:block">
              <img 
                src="https://img.freepik.com/free-photo/stylish-business-persons-with-smartphone_23-2147626434.jpg?t=st=1763514426~exp=1763518026~hmac=7ab2a6ee6fd7e5a94f613348b4b856766a55802ff765e969592aa1291dd451fd&w=1060" 
                alt="Happy customer" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white dark:bg-dark-800 border-y border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose UniversalTrustCU?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              We're not just another bank. We're a member-owned credit union dedicated to your financial success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <div key={index} className="bg-white dark:bg-dark-800 p-6 rounded-xl border border-gray-200 dark:border-dark-700">
                <product.icon className="w-10 h-10 text-blue-600 dark:text-blue-400 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{product.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{product.description}</p>
                <ul className="space-y-2">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image + Benefits Section */}
      <section className="py-20 bg-white dark:bg-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=700&h=700&fit=crop" 
                alt="Mobile banking" 
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Everything You Need in One Place
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                UniversalTrustCU offers comprehensive banking services with unmatched benefits and industry-leading security.
              </p>
              <div className="grid grid-cols-1 gap-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Lock className="w-16 h-16 mb-6" />
              <h2 className="text-4xl font-bold mb-6">Your Security is Our Priority</h2>
              <p className="text-gray-300 mb-8 text-lg">
                We use bank-grade security measures to protect your financial information. Your money and data are safe with us.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                  <div className="text-4xl font-bold mb-2">256-bit</div>
                  <div className="text-sm text-gray-300">SSL Encryption</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                  <div className="text-4xl font-bold mb-2">2FA</div>
                  <div className="text-sm text-gray-300">Multi-Factor Auth</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                  <div className="text-4xl font-bold mb-2">$0</div>
                  <div className="text-sm text-gray-300">Fraud Liability</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                  <div className="text-4xl font-bold mb-2">24/7</div>
                  <div className="text-sm text-gray-300">Monitoring</div>
                </div>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=700&h=700&fit=crop" 
                alt="Security" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20 bg-blue-50 dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Smartphone className="w-16 h-16 text-blue-600 dark:text-blue-400 mb-6" />
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Bank On The Go
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                Download our award-winning mobile app and take your bank with you everywhere.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Mobile Check Deposit</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Deposit checks instantly with your phone</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Biometric Login</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Face ID and fingerprint authentication</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Card Controls</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Freeze and unfreeze your card instantly</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="App Store" className="h-12" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-12" />
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=900&fit=crop" 
                alt="Mobile app" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <Users className="w-20 h-20 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Switch to Better Banking?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join over 250,000 members who trust UniversalTrustCU. Open your free account today and experience banking without the fees.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors text-lg shadow-xl"
          >
            Open Your Free Account
            <ArrowRight className="w-6 h-6" />
          </Link>
          <p className="mt-6 text-blue-100">
            <CheckCircle className="w-5 h-5 inline mr-2" />
            No minimum deposit • No monthly fees • FDIC insured up to $250,000
          </p>
        </div>
      </section>
    </div>
  )
}

export default Home